import { useState, useCallback } from 'react';
import { isServiceable } from '../data/serviceablePincodes';

/**
 * Custom hook for pincode validation
 * Provides reusable validation logic across components
 */
export const usePincodeValidation = () => {
    const [validationResult, setValidationResult] = useState(null);
    const [isValidating, setIsValidating] = useState(false);

    /**
     * Validate a pincode
     * @param {string} pincode - 6-digit pincode to validate
     * @returns {Object} - { valid, district, message }
     */
    const validate = useCallback((pincode) => {
        if (!pincode || pincode.length !== 6) {
            const result = { valid: false, message: 'Please enter a valid 6-digit pincode' };
            setValidationResult(result);
            return result;
        }

        setIsValidating(true);
        const result = isServiceable(pincode);
        setValidationResult(result);
        setIsValidating(false);
        return result;
    }, []);

    /**
     * Extract pincode from an address string
     * Finds the last 6-digit number in the string
     * @param {string} address - Full address string
     * @returns {string|null} - Extracted pincode or null
     */
    const extractPincodeFromAddress = useCallback((address) => {
        if (!address) return null;
        // Find all 6-digit numbers in the address
        const matches = address.match(/\b\d{6}\b/g);
        // Return the last match (typically pincode is at the end)
        return matches ? matches[matches.length - 1] : null;
    }, []);

    /**
     * Validate pincode extracted from address
     * @param {string} address - Full address string
     * @returns {Object} - { valid, district, message, pincode }
     */
    const validateAddress = useCallback((address) => {
        const pincode = extractPincodeFromAddress(address);
        if (!pincode) {
            const result = {
                valid: false,
                message: 'No valid pincode found in address. Please include a 6-digit pincode.',
                pincode: null
            };
            setValidationResult(result);
            return result;
        }
        const result = validate(pincode);
        return { ...result, pincode };
    }, [extractPincodeFromAddress, validate]);

    /**
     * Reset validation state
     */
    const reset = useCallback(() => {
        setValidationResult(null);
        setIsValidating(false);
    }, []);

    return {
        validationResult,
        isValidating,
        validate,
        validateAddress,
        extractPincodeFromAddress,
        reset
    };
};

export default usePincodeValidation;
